import React, { useState } from 'react';
import { useRecordings } from '../../contexts/RecordingContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Copy,
  Check,
  Lock,
  Globe,
  Calendar,
  Eye,
  Download,
  Loader2,
} from 'lucide-react';

interface ShareDialogProps {
  recording: any;
  open: boolean;
  onClose: () => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  recording,
  open,
  onClose,
}) => {
  const { createShareLink } = useRecordings();
  const [isCreating, setIsCreating] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // Share options
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiryHours, setExpiryHours] = useState(24);
  const [useMaxViews, setUseMaxViews] = useState(false);
  const [maxViews, setMaxViews] = useState(100);
  const [allowDownloads, setAllowDownloads] = useState(false);

  const handleCreateLink = async () => {
    setIsCreating(true);
    try {
      const options: any = {
        allowDownloads,
      };
      
      if (usePassword && password) {
        options.password = password;
      }
      
      if (useExpiry) {
        options.expiresIn = expiryHours;
      }
      
      if (useMaxViews) {
        options.maxViews = maxViews;
      }
      
      const result = await createShareLink(recording.id, options);
      setShareLink(result.shareUrl);
    } catch (err) {
      console.error('Failed to create share link:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareLink(null);
    setPassword('');
    setUsePassword(false);
    setUseExpiry(false);
    setUseMaxViews(false);
    setAllowDownloads(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share Recording</DialogTitle>
          <DialogDescription>
            Create a secure link to share "{recording.title}"
          </DialogDescription>
        </DialogHeader>
        
        {!shareLink ? (
          <div className="space-y-6">
            {/* Password Protection */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="password-protect" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Password Protection
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Require a password to access
                </p>
              </div>
              <Switch
                id="password-protect"
                checked={usePassword}
                onCheckedChange={setUsePassword}
              />
            </div>
            
            {usePassword && (
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
            
            {/* Expiry */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="expiry" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Link Expiry
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Automatically expire after time
                </p>
              </div>
              <Switch
                id="expiry"
                checked={useExpiry}
                onCheckedChange={setUseExpiry}
              />
            </div>
            
            {useExpiry && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="720"
                  value={expiryHours}
                  onChange={(e) => setExpiryHours(parseInt(e.target.value))}
                  className="w-24"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">hours</span>
              </div>
            )}
            
            {/* Max Views */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="max-views" className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  View Limit
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Limit number of views
                </p>
              </div>
              <Switch
                id="max-views"
                checked={useMaxViews}
                onCheckedChange={setUseMaxViews}
              />
            </div>
            
            {useMaxViews && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="10000"
                  value={maxViews}
                  onChange={(e) => setMaxViews(parseInt(e.target.value))}
                  className="w-24"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">views</span>
              </div>
            )}
            
            {/* Allow Downloads */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="downloads" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Allow Downloads
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Recipients can download the file
                </p>
              </div>
              <Switch
                id="downloads"
                checked={allowDownloads}
                onCheckedChange={setAllowDownloads}
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleCreateLink} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Link'
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-800 dark:text-green-300">
                Share link created successfully!
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label>Share Link</Label>
              <div className="flex gap-2">
                <Input
                  value={shareLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            
            {usePassword && (
              <Alert>
                <Lock className="h-4 w-4" />
                <AlertDescription>
                  Password: {password}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
              {useExpiry && (
                <p>• Link expires in {expiryHours} hours</p>
              )}
              {useMaxViews && (
                <p>• Limited to {maxViews} views</p>
              )}
              {allowDownloads && (
                <p>• Downloads enabled</p>
              )}
            </div>
            
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;